<template>
  <div class="app">
    <div id="map-wrapper">
      <HeaderMenu v-show="!showAppsModal" />
      <ArticleDrawer />
      <SidebarDrawer :themes="themes" :routes="routes" />
      <FeaturedArticlesDrawer />
      <FooterMenu />
      <div id="map" />

      <v-snackbar id="error-404" v-model="error404" height="30" :timeout="3000">
        <p>
          {{ $t("error.404") }}
        </p>
      </v-snackbar>

      <div
        id="footer-gradient"
        :style="{
          'background-image': 'url(' + `${getFooterGradient()}` + ')'
        }"
      ></div>
    </div>
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import HeaderMenu from "@/components/menus/HeaderMenu.vue";
import SidebarDrawer from "@/components/menus/SidebarDrawer.vue";
import FeaturedArticlesDrawer from "@/components/FeaturedArticlesDrawer.vue";
import ArticleDrawer from "@/components/ArticleDrawer.vue";
import FooterMenu from "@/components/menus/FooterMenu.vue";
import { mapActions, mapState, mapGetters, mapMutations } from "vuex";
import {
  appIdentifier,
  getMapStyle,
  getMapBounds,
  getMapCenter,
  getMapLineColor
} from "@/utils/appIdentifier.js";

function emptyGeoJson() {
  return {
    type: "FeatureCollection",
    features: []
  };
}

export default {
  name: "MapboxView",
  components: {
    HeaderMenu,
    ArticleDrawer,
    FooterMenu,
    SidebarDrawer,
    FeaturedArticlesDrawer
  },
  props: {
    id: {
      type: String
    },
    error404: {
      type: Boolean
    }
  },
  data: function() {
    return {
      map: null,
      currentThemeMarkers: [],
      currentRouteMarkers: []
    };
  },
  activated() {
    this.map.resize();
  },
  mounted() {
    this.$store.commit(this.$mutations.CLEAR_ARTICLE_STACK);
    this.getThemes();
    this.getRoutes();
    this.getHotspots();
    this.createMap();

    //Show article by id (article/id)
    if (this.id) {
      this.$store.dispatch(this.$actions.GET_ARTICLE, this.id).then(() => {
        this.$store.commit(this.$mutations.TOGGLE_ARTICLE_DRAWER, true);
      });
    }

    //Event listeners
    this.$eventBus.$on("CENTER_MAP", () => {
      this.focusOnStartPosition();
    });
  },
  beforeDestroy() {
    this.$store.commit(this.$mutations.RESET_SIDEBAR_MENU);
  },
  computed: {
    ...mapState({
      locale: state => state.locale,
      hotspots: state => state.hotspots,
      theme: state => state.theme,
      themes: state => state.themes,
      route: state => state.route,
      routes: state => state.routes,
      selectedArticle: state => state.selectedArticle,
      showAppsModal: state => state.showAppsModal
    }),
    ...mapGetters({ mapPois: "getMapPois", routePois: "getRoutePois" })
  },
  watch: {
    locale: function() {
      this.getThemes();
      this.getRoutes();
      this.getHotspots();
      this.removeThemePois();
      this.removeRoutePois();
      this.resetThemes();
      this.resetRoutes();
      this.resetTheme();
      this.resetRoute();
    },
    theme: function() {
      if (Object.entries(this.theme).length > 0) {
        this.setThemePois();
      } else {
        this.removeThemePois();
      }
    },
    route: function() {
      if (Object.entries(this.route).length > 0) {
        this.setRoutePois();
      } else {
        this.removeRoutePois();
      }
    }
  },
  methods: {
    ...mapActions(["getThemes", "getRoutes", "getHotspots"]),
    ...mapMutations(["resetThemes", "resetTheme", "resetRoutes", "resetRoute"]),
    getAppName() {
      return appIdentifier();
    },
    getFooterGradient() {
      return `${require(`@/assets/svg/${this.getAppName()}/map-footer-gradiant.svg`)}`;
    },
    setThemePois() {
      this.mapPois.forEach(mapPoi => {
        let poiMarker = document.createElement("div");
        poiMarker.className = `marker-5d-${this.getAppName()}`;
        poiMarker.addEventListener("click", () => {
          //Check for selected article being open
          if (mapPoi.article_id == this.selectedArticle?.id) {
            this.$store.commit(this.$mutations.TOGGLE_MENU, {
              name: "articleDrawer"
            });
          } else {
            this.$store.commit(this.$mutations.TOGGLE_MENU, {
              name: "articleDrawer"
            });
            this.$store.dispatch(this.$actions.GET_ARTICLE, mapPoi.article_id);
          }
        });

        let marker = new mapboxgl.Marker(poiMarker)
          .setLngLat([mapPoi.lon, mapPoi.lat])
          .addTo(this.map);
        this.currentThemeMarkers.push(marker);
      });
    },
    removeThemePois() {
      for (let i = this.currentThemeMarkers.length - 1; i >= 0; i--) {
        this.currentThemeMarkers[i].remove();
      }
    },
    setRoutePois() {
      this.routePois.forEach(routePoi => {
        let poiMarker = document.createElement("div");
        poiMarker.className = `marker-5d-${this.getAppName()}`;
        poiMarker.addEventListener("click", () => {
          //Check for selected article being open
          if (routePoi.article_id == this.selectedArticle?.id) {
            this.$store.commit(this.$mutations.TOGGLE_MENU, {
              name: "articleDrawer"
            });
          } else {
            this.$store.commit(this.$mutations.TOGGLE_MENU, {
              name: "articleDrawer"
            });
            this.$store.dispatch(
              this.$actions.GET_ARTICLE,
              routePoi.article_id
            );
          }
        });

        let marker = new mapboxgl.Marker(poiMarker)
          .setLngLat([routePoi.lon, routePoi.lat])
          .addTo(this.map);
        this.currentRouteMarkers.push(marker);
      });

      this.map.fitBounds([
        [this.route.map_bottom_left_lon, this.route.map_bottom_left_lat],
        [this.route.map_top_right_lon, this.route.map_top_right_lat]
      ]);

      this.map.getSource("route").setData(this.route.route_geo_json);
    },
    removeRoutePois() {
      for (let i = this.currentRouteMarkers.length - 1; i >= 0; i--) {
        this.currentRouteMarkers[i].remove();
      }

      this.map.getSource("route").setData(emptyGeoJson());
    },
    focusOnStartPosition() {
      this.map.fitBounds(getMapBounds());
    },
    createMap() {
      mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_API_KEY;

      // Initialize the map
      this.map = new mapboxgl.Map({
        container: "map",
        style: getMapStyle(),
        minZoom: 6,
        maxZoom: 20,
        center: getMapCenter(),
        zoom: 16,
        bearing: 0 // bearing in degrees
      });

      this.focusOnStartPosition();

      //Add Hotspot Markers to map
      this.map.on("load", () => {
        this.map.addSource("route", {
          type: "geojson",
          data: emptyGeoJson()
        });

        this.map.addLayer({
          id: "route",
          type: "line",
          source: "route",
          layout: {
            "line-join": "round",
            "line-cap": "round"
          },
          paint: {
            "line-color": getMapLineColor(),
            "line-dasharray": [2, 2],
            "line-width": 4
          }
        });

        this.hotspots.forEach(hotspot => {
          let markerHotspot = document.createElement("div");
          markerHotspot.className = `marker-panorama-${this.getAppName()}`;
          markerHotspot.addEventListener("click", () => {
            this.$router.push({
              name: "panorama-view",
              params: {
                id: hotspot.id
              }
            });
          });

          new mapboxgl.Marker(markerHotspot)
            .setLngLat([hotspot.lon, hotspot.lat])
            .addTo(this.map);
        });
      });
    }
  }
};
</script>

<style lang="scss">
#error-404 {
  z-index: 100;

  p {
    margin: 0;
  }
}

#map-wrapper {
  height: 100vh;
  width: 100%;
}

#map {
  position: absolute;
  top: 0;
  height: 100%;
  width: 100%;

  .mapboxgl-ctrl-attrib {
    background: none;
  }

  .marker-panorama-huygens {
    width: 50px;
    height: 50px;
    background: url("../assets/svg/huygens/icon-marker-hotspot.svg");
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    cursor: pointer;
  }

  .marker-5d-huygens {
    width: 30px;
    height: 30px;
    background: url("../assets/svg/huygens/icon-marker-5d.svg");
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    cursor: pointer;
  }

  .marker-panorama-dordrecht5D {
    width: 40px;
    height: 40px;
    background-image: url("../assets/svg/dordrecht5D/icon-marker-hotspot.svg");
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    cursor: pointer;
  }

  .marker-5d-dordrecht5D {
    width: 30px;
    height: 30px;
    background-image: url("../assets/svg/dordrecht5D/icon-marker-5d.svg");
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    cursor: pointer;
  }

  .marker-panorama-canals5D {
    width: 40px;
    height: 40px;
    background-image: url("../assets/svg/canals5D/icon-marker-hotspot.svg");
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    cursor: pointer;
  }

  .marker-5d-canals5D {
    width: 30px;
    height: 30px;
    background-image: url("../assets/svg/canals5D/icon-marker-5d.svg");
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    cursor: pointer;
  }
}

#footer-gradient {
  background-size: contain;
  background-repeat: repeat-x;
  width: 100%;
  height: 100px;
  position: fixed;
  bottom: 0;
}
</style>
